import React, { useEffect, useRef, useState } from "react";
import { useStoreSelector } from "~/lib/store/hooks";
import {
  tenantOnboardingActions,
  tenantOnboardingStore,
} from "../onboarding/store/tenantOnboardingStore";
import { validateStep1 } from "../onboarding/validation/step1Validation";
import { TenantInfoForm } from "../onboarding/components/Step1TenantInfo/TenantInfoForm";
import { Card } from "primereact/card";
import { useParams } from "react-router-dom";
import CustomeButton from "~/components/Button";
import {
  useGetTenantsId,
  usePatchTenantsId,
  type GetTenantsId200Data,
} from "~/api/generated/api";
import ToastMessage from "~/components/ToastMessage";
import TenantAuditSidebar from "~/components/audit-sidebar/TenantAuditSidebar";
import AdminOnly from "~/components/AdminOnly";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Transform API response to form data format
 * API returns dates as ISO strings, form expects Date objects or null
 * Note: PATCH /tenants/{id} only returns {tenant_id, updated_fields}.
 * After save, invalidateQueries triggers a GET re-fetch which re-populates the form via useEffect.
 */
const transformApiToFormData = (apiData: GetTenantsId200Data): any => {
  return {
    ...apiData,
    billing_start_date: apiData.billing_start_date
      ? new Date(apiData.billing_start_date)
      : null,
    billing_end_date: apiData.billing_end_date
      ? new Date(apiData.billing_end_date)
      : null,
  };
};

/**
 * Step 1: General Information Container
 */
const TenantGeneralInformation: React.FC = () => {
  const { id: tenantId } = useParams();
  const { data: tenantResponse, isLoading } = useGetTenantsId(tenantId!);
  const tenantData = tenantResponse?.data?.data;
  const queryClient = useQueryClient();
  const toastRef = useRef(null);
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const step1Data = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.step1Data,
  );

  const generalInformation = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.generalInformation,
  );

  const domainStatus = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.domainStatus,
  );

  const validationErrors = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.validationErrors,
  );

  const touchedFields = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.touchedFields,
  );

  const isEditing = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.isEditing,
  );

  const handleEnableEdit = () => {
    tenantOnboardingActions.setIsEditing(true);
  };

  const handleDiscardEdit = () => {
    tenantOnboardingActions.updateStep1Data(generalInformation || {});
    tenantOnboardingActions.setIsDirty(false);
    tenantOnboardingActions.setIsEditing(false);
    tenantOnboardingActions.clearTouchedFields();
    tenantOnboardingActions.setValidationErrors({});
  };

  const handleSaveChanges = async () => {
    tenantOnboardingActions.setGeneralInformation(step1Data);
    tenantOnboardingActions.setIsDirty(false);
    handleSave();
  };

  const handleChange = (updates: Partial<typeof step1Data>) => {
    const fieldName = Object.keys(updates)[0];

    const newState = { ...step1Data, ...updates };
    tenantOnboardingActions.updateStep1Data(updates);

    tenantOnboardingActions.markFieldTouched(fieldName);

    const errors = validateStep1(newState);

    const filteredErrors: Record<string, string[]> = {};
    Object.keys(errors).forEach((key) => {
      if (touchedFields.has(key) || key === fieldName) {
        filteredErrors[key] = errors[key];
      }
    });
    tenantOnboardingActions.setValidationErrors(filteredErrors);
    if (updates.domains) {
      tenantOnboardingActions.markFieldTouched("domains");
    }
  };

  const handleBlur = (fieldName: string) => {
    tenantOnboardingActions.markFieldTouched(fieldName);
    const errors = validateStep1(step1Data);
    const filteredErrors: Record<string, string[]> = {};
    Object.keys(errors).forEach((key) => {
      if (touchedFields.has(key) || fieldName === key) {
        filteredErrors[key] = errors[key];
      }
    });

    tenantOnboardingActions.setValidationErrors(filteredErrors);
  };

  useEffect(() => {
    if (tenantData) {
      const formData = transformApiToFormData(tenantData);
      tenantOnboardingActions.setStep1Data(formData);
    }
  }, [tenantData]);
  function isEqual(a: any, b: any) {
    return (
      JSON.stringify(a, (key, value) =>
        value instanceof Date ? value.toISOString() : value,
      ) ===
      JSON.stringify(b, (key, value) =>
        value instanceof Date ? value.toISOString() : value,
      )
    );
  }

  const hasChanges = !isEqual(step1Data, generalInformation);

  const fieldErrors = validateStep1(step1Data);
  const hasValidationErrors = Object.keys(fieldErrors).length > 0;

  const hasDomainIssues = Object.values(domainStatus).some(
    (s) => s.status === "unavailable" || s.status === "invalid",
  );

  const canSave = hasChanges && !hasValidationErrors && !hasDomainIssues;

  const editTenant = usePatchTenantsId({
    mutation: {
      onSuccess: (response) => {
        tenantOnboardingActions.setIsEditing(false);
        // Update query cache directly with PATCH response data (full tenant object)
        queryClient.setQueryData(["getTenantsId", tenantId], response);
        // Invalidate audit logs cache to refresh after update
        queryClient.invalidateQueries({
          queryKey: ["tenant-audit", tenantId],
        });
        (toastRef?.current as any)?.show({
          severity: "success",
          summary: "Tenant Information",
          detail: "has been updated!",
          icon: "pi pi-check",
        });
      },
    },
  });

  const handleSave = () => {
    // Clean up step1Data to match API expectations
    const cleanData = {
      ...step1Data,
      total_number_of_users: step1Data.total_number_of_users || undefined,
    };
    editTenant.mutate({ id: tenantId!, data: cleanData as any });
  };

  const auditLabels = () => {
    return {
      create: "Tenant Created",
      update: "Information Updated",
      delete: "Tenant Deleted",
    };
  };

  return (
    <Card className="gen-card">
      <div className="flex align-items-center justify-content-between">
        <p className="heading">General Information</p>
        <div className="flex gap-1">
          {isEditing ? (
            <>
              <CustomeButton
                label="Discard"
                variant="outlined"
                className="mr-2"
                onClick={handleDiscardEdit}
              />

              <CustomeButton
                label="Save Changes"
                variant="contained"
                onClick={handleSaveChanges}
                loading={editTenant.isPending}
                disabled={!canSave || editTenant.isPending}
                className={!canSave ? "disabled-btn" : ""}
                iconPos={"right"}
              />
            </>
          ) : (
            <>
              <AdminOnly>
                <CustomeButton
                  label="View History"
                  variant="outlined"
                  className="mr-2"
                  onClick={() => setAuditSidebarVisible(true)}
                  icon="pi pi-history"
                  iconPos="right"
                />
              </AdminOnly>
              <CustomeButton
                label="Edit"
                variant="contained"
                className="mr-2 access-btn"
                onClick={handleEnableEdit}
                icon="pi pi-pencil"
                iconPos="right"
              />
            </>
          )}
        </div>
      </div>
      <TenantInfoForm
        data={step1Data}
        onChange={handleChange}
        onBlur={handleBlur}
        errors={validationErrors}
        touchedFields={touchedFields}
        setFieldError={(fieldName, messages) =>
          tenantOnboardingActions.setValidationErrors({
            ...validationErrors,
            [fieldName]: messages,
          })
        }
        clearFieldError={(fieldName) =>
          tenantOnboardingActions.clearFieldError(fieldName)
        }
        markFieldTouched={(fieldName) =>
          tenantOnboardingActions.markFieldTouched(fieldName)
        }
        isEditing={true}
        disabled={!isEditing}
        generalInformation={generalInformation}
        loading={isLoading}
      />
      <ToastMessage ref={toastRef} />
      {isAuditSidebarVisible && (
        <TenantAuditSidebar
          heading="General Information"
          tenantId={tenantId!}
          setSidebarToggle={setAuditSidebarVisible}
          sidebarToggle={isAuditSidebarVisible}
          labels={auditLabels}
        />
      )}
    </Card>
  );
};

export default TenantGeneralInformation;
